import { useState, useEffect } from 'react';
import { objectsAPI } from '@/lib/api';
import api, { getErrorMessage } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { 
  Settings as SettingsIcon, 
  Building2, 
  Clock, 
  Loader2,
  Save,
  Coffee,
  UtensilsCrossed,
  User,
  Lock,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

function SuperAdminSettings({ t, initialTab }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab || 'profile');
  const [profileForm, setProfileForm] = useState({ name: user?.name || '', email: user?.email || '' });
  const [pwdForm, setPwdForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/auth/profile', null, { params: { name: profileForm.name, email: profileForm.email } });
      toast.success(t('settings.profileSaved') || 'Profile saved');
    } catch (e) {
      toast.error(getErrorMessage(e, t('msg.failedUpdate'), t));
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (pwdForm.new_password !== pwdForm.confirm) {
      toast.error(t('settings.passwordMismatch') || 'Passwords do not match');
      return;
    }
    if (pwdForm.new_password.length < 8) {
      toast.error(t('settings.passwordTooShort') || 'Password must be at least 8 characters');
      return;
    }
    if (!/[a-zA-Z]/.test(pwdForm.new_password) || !/[0-9]/.test(pwdForm.new_password)) {
      toast.error(t('settings.passwordWeak') || 'Password must contain letters and numbers');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/change-password', null, { params: { old_password: pwdForm.old_password, new_password: pwdForm.new_password } });
      toast.success(t('settings.passwordChanged') || 'Password changed successfully');
      setPwdForm({ old_password: '', new_password: '', confirm: '' });
    } catch (e) {
      toast.error(getErrorMessage(e, t('settings.wrongOldPassword') || 'Wrong current password', t));
    } finally { setSaving(false); }
  };

  const tabs = [
    { id: 'profile', label: t('settings.profile') || 'Profile', icon: User },
    { id: 'security', label: t('settings.security') || 'Security', icon: Lock },
  ];

  return (
    <div className="space-y-6 max-w-xl">
      <h1 className="text-3xl font-bold text-white font-heading flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
          <Shield className="h-5 w-5 text-rose-400" />
        </div>
        {t('nav.settings')}
      </h1>

      <div className="flex gap-1 bg-wp-dark/40 rounded-xl p-1 w-fit">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-2 px-5 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-wp-medium text-white shadow' : 'text-wp-light/50 hover:text-wp-light'}`}>
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'profile' && (
        <div className="glass-card rounded-2xl p-6 space-y-5">
          <h3 className="text-white font-medium flex items-center gap-2"><User className="h-4 w-4 text-wp-light" />{t('settings.profile') || 'Profile'}</h3>
          <div className="space-y-2">
            <Label className="text-wp-light/70">{t('superAdmin.userName') || 'Name'}</Label>
            <Input value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
          </div>
          <div className="space-y-2">
            <Label className="text-wp-light/70">{t('common.email')}</Label>
            <Input type="email" value={profileForm.email} onChange={e => setProfileForm({...profileForm, email: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
          </div>
          <Button onClick={handleSaveProfile} disabled={saving} className="bg-wp-medium hover:bg-wp-light text-white gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t('common.save')}
          </Button>
        </div>
      )}

      {activeTab === 'security' && (
        <div className="glass-card rounded-2xl p-6 space-y-5">
          <h3 className="text-white font-medium flex items-center gap-2"><Lock className="h-4 w-4 text-wp-light" />{t('settings.changePassword') || 'Change Password'}</h3>
          {[
            { key: 'old_password', label: t('settings.currentPassword') || 'Current password', show: showOld, toggle: () => setShowOld(v => !v) },
            { key: 'new_password', label: t('settings.newPassword') || 'New password', show: showNew, toggle: () => setShowNew(v => !v) },
            { key: 'confirm',      label: t('settings.confirmPassword') || 'Confirm password', show: showConfirm, toggle: () => setShowConfirm(v => !v) },
          ].map(({ key, label, show, toggle }) => (
            <div key={key} className="space-y-2">
              <Label className="text-wp-light/70">{label}</Label>
              <div className="relative">
                <Input
                  type={show ? 'text' : 'password'}
                  value={pwdForm[key]}
                  onChange={e => setPwdForm({...pwdForm, [key]: e.target.value})}
                  placeholder="••••••••"
                  className="bg-wp-dark/30 border-wp-dark/50 text-white pr-10"
                />
                <button type="button" onClick={toggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-wp-light/40 hover:text-wp-light transition-colors">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          ))}
          <p className="text-wp-light/40 text-xs">{t('settings.passwordHint') || 'Minimum 8 characters, letters and numbers'}</p>
          <Button onClick={handleChangePassword} disabled={saving || !pwdForm.old_password || !pwdForm.new_password || !pwdForm.confirm} className="bg-wp-medium hover:bg-wp-light text-white gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {t('settings.changePassword') || 'Change Password'}
          </Button>
        </div>
      )}
    </div>
  );
}

function AdminSettings({ t }) {
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { id: 'profile', label: t('settings.tabProfile') || 'Profile', icon: User },
    { id: 'security', label: t('settings.tabSecurity') || 'Security', icon: Lock },
    { id: 'objects', label: t('settings.tabObjects') || 'Object Rules', icon: Building2 },
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-white font-heading flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-wp-medium/20 flex items-center justify-center">
            <SettingsIcon className="h-6 w-6 text-wp-light" />
          </div>
          {t('settings.title')}
        </h1>
        <p className="text-wp-light/40 mt-2">{t('settings.subtitle')}</p>
      </div>
      <div className="flex gap-2 bg-wp-dark/30 p-1 rounded-xl border border-wp-dark/50 w-full overflow-x-auto scrollbar-hide">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === id ? 'bg-wp-medium text-white shadow-lg' : 'text-wp-light/50 hover:text-wp-light'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>
      {activeTab === 'profile' && <SuperAdminSettings t={t} initialTab="profile" />}
      {activeTab === 'security' && <SuperAdminSettings t={t} initialTab="security" />}
      {activeTab === 'objects' && <AdminObjectRules t={t} />}
    </div>
  );
}

export default function Settings() {
  const { t } = useLanguage();
  const { user } = useAuth();

  if (user?.role === 'super_admin') return <SuperAdminSettings t={t} />;
  if (user?.role === 'admin') return <AdminSettings t={t} />;

  return (
    <div className="flex items-center justify-center h-64">
      <div className="glass-card rounded-2xl p-12 text-center">
        <SettingsIcon className="h-12 w-12 mx-auto text-white/20 mb-4" />
        <p className="text-wp-light/40">{t('superAdmin.accessDenied')}</p>
      </div>
    </div>
  );
}

function AdminObjectRules({ t }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [objects, setObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState('');
  const [rules, setRules] = useState({
    grace_period_minutes: 5,
    pause_enabled: true,
    pause_max_times_per_hour: 3,
    pause_max_minutes_per_shift: 30,
    lunch_enabled: true,
    lunch_mandatory: false,
    lunch_min_duration: 30,
    lunch_max_duration: 60
  });

  useEffect(() => {
    loadObjects();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedObject) {
      loadObjectRules();
    }
  }, [selectedObject]);

  const loadObjects = async () => {
    try {
      const res = await objectsAPI.list();
      setObjects(res.data);
      if (res.data.length > 0) {
        setSelectedObject(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load objects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadObjectRules = async () => {
    try {
      const res = await objectsAPI.get(selectedObject);
      const obj = res.data;
      setRules({
        grace_period_minutes: obj.rules?.grace_period_minutes || 5,
        pause_enabled: obj.rules?.pause_rules?.enabled ?? true,
        pause_max_times_per_hour: obj.rules?.pause_rules?.max_times_per_hour || 3,
        pause_max_minutes_per_shift: obj.rules?.pause_rules?.max_minutes_per_shift || 30,
        lunch_enabled: obj.rules?.lunch_rules?.enabled ?? true,
        lunch_mandatory: obj.rules?.lunch_rules?.mandatory || false,
        lunch_min_duration: obj.rules?.lunch_rules?.min_duration_minutes || 30,
        lunch_max_duration: obj.rules?.lunch_rules?.max_duration_minutes || 60
      });
    } catch (error) {
      console.error('Failed to load object rules:', error);
    }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      await objectsAPI.update(selectedObject, {
        rules: {
          grace_period_minutes: parseInt(rules.grace_period_minutes),
          pause_rules: {
            enabled: rules.pause_enabled,
            max_times_per_hour: parseInt(rules.pause_max_times_per_hour),
            max_minutes_per_shift: parseInt(rules.pause_max_minutes_per_shift)
          },
          lunch_rules: {
            enabled: rules.lunch_enabled,
            mandatory: rules.lunch_mandatory,
            min_duration_minutes: parseInt(rules.lunch_min_duration),
            max_duration_minutes: parseInt(rules.lunch_max_duration)
          }
        }
      });
      toast.success(t('settings.saveSuccess'));
    } catch (error) {
      console.error('Failed to save rules:', error);
      toast.error(t('settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <Loader2 className="h-10 w-10 animate-spin text-wp-light" />
          <div className="absolute inset-0 blur-xl bg-wp-medium/30 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Object Selector */}
      <div className="glass-card rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-wp-medium/20 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-wp-light" />
          </div>
          <h2 className="text-lg font-semibold text-white font-heading">{t('common.object')}</h2>
        </div>
        
        {objects.length === 0 ? (
          <p className="text-wp-light/40">{t('common.noData')}</p>
        ) : (
          <Select value={selectedObject} onValueChange={setSelectedObject}>
            <SelectTrigger className="w-full max-w-md bg-wp-dark/30 border-wp-dark/50 text-white">
              <SelectValue placeholder={t('common.object')} />
            </SelectTrigger>
            <SelectContent className="bg-wp-darker border-wp-dark/50">
              {objects.map(obj => (
                <SelectItem key={obj.id} value={obj.id}>
                  <span className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {obj.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {selectedObject && (
        <>
          {/* Grace Period */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-wp-medium/20 flex items-center justify-center">
                <Clock className="h-5 w-5 text-wp-light" />
              </div>
              <h2 className="text-lg font-semibold text-white font-heading">{t('objects.gracePeriod')}</h2>
            </div>
            
            <div className="space-y-2 max-w-md">
              <Label className="text-wp-light/70">{t('objects.gracePeriod')}</Label>
              <Input
                type="number"
                min="0"
                max="30"
                value={rules.grace_period_minutes}
                onChange={(e) => setRules({...rules, grace_period_minutes: e.target.value})}
                className="bg-wp-dark/30 border-wp-dark/50 text-white"
              />
            </div>
          </div>

          {/* Pause Rules */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Coffee className="h-5 w-5 text-amber-400" />
              </div>
              <h2 className="text-lg font-semibold text-white font-heading">{t('objects.pauseAllowed')}</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between max-w-md p-4 rounded-xl bg-wp-dark/30 border border-wp-dark/50">
                <div>
                  <Label className="text-white">{t('objects.pauseAllowed')}</Label>
                </div>
                <Switch
                  checked={rules.pause_enabled}
                  onCheckedChange={(v) => setRules({...rules, pause_enabled: v})}
                />
              </div>

              {rules.pause_enabled && (
                <div className="grid sm:grid-cols-2 gap-4 max-w-md">
                  <div className="space-y-2">
                    <Label className="text-wp-light/70">{t('objects.maxTimesHour')}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={rules.pause_max_times_per_hour}
                      onChange={(e) => setRules({...rules, pause_max_times_per_hour: e.target.value})}
                      className="bg-wp-dark/30 border-wp-dark/50 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-wp-light/70">{t('objects.maxMinutesShift')}</Label>
                    <Input
                      type="number"
                      min="5"
                      max="120"
                      value={rules.pause_max_minutes_per_shift}
                      onChange={(e) => setRules({...rules, pause_max_minutes_per_shift: e.target.value})}
                      className="bg-wp-dark/30 border-wp-dark/50 text-white"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lunch Rules */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-wp-light/20 flex items-center justify-center">
                <UtensilsCrossed className="h-5 w-5 text-wp-light" />
              </div>
              <h2 className="text-lg font-semibold text-white font-heading">{t('objects.lunchAllowed')}</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between max-w-md p-4 rounded-xl bg-wp-dark/30 border border-wp-dark/50">
                <div>
                  <Label className="text-white">{t('objects.lunchAllowed')}</Label>
                </div>
                <Switch
                  checked={rules.lunch_enabled}
                  onCheckedChange={(v) => setRules({...rules, lunch_enabled: v})}
                />
              </div>

              {rules.lunch_enabled && (
                <>
                  <div className="flex items-center justify-between max-w-md p-4 rounded-xl bg-wp-dark/30 border border-wp-dark/50">
                    <div>
                      <Label className="text-white">{t('objects.lunchMandatory')}</Label>
                    </div>
                    <Switch
                      checked={rules.lunch_mandatory}
                      onCheckedChange={(v) => setRules({...rules, lunch_mandatory: v})}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 max-w-md">
                    <div className="space-y-2">
                      <Label className="text-wp-light/70">{t('objects.minDuration')}</Label>
                      <Input
                        type="number"
                        min="15"
                        max="120"
                        value={rules.lunch_min_duration}
                        onChange={(e) => setRules({...rules, lunch_min_duration: e.target.value})}
                        className="bg-wp-dark/30 border-wp-dark/50 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-wp-light/70">{t('objects.maxDuration')}</Label>
                      <Input
                        type="number"
                        min="30"
                        max="180"
                        value={rules.lunch_max_duration}
                        onChange={(e) => setRules({...rules, lunch_max_duration: e.target.value})}
                        className="bg-wp-dark/30 border-wp-dark/50 text-white"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button 
              onClick={saveRules} 
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-wp-medium hover:bg-wp-light text-white font-medium rounded-xl transition-all disabled:opacity-50"
              data-testid="save-settings-button"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {t('common.save')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
